import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdArrowBack, MdPeople, MdEdit, MdPause, MdPlayArrow, MdPersonRemove,
  MdSearch, MdCalendarToday, MdCheckCircle,
} from 'react-icons/md';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import BeltBadge from '../../components/common/BeltBadge';
import { SkeletonRow } from '../../components/common/Skeleton';
import { getClass } from '../../services/classService';
import { getClassEnrollments, unenroll, pauseEnrollment, resumeEnrollment } from '../../services/enrollmentService';
import { getMonthlyReport } from '../../services/attendanceService';
import { useCalendar } from '../../context/CalendarContext';
import DualDate from '../../components/common/DualDate';
import EnrollStudentsModal from '../../components/admin/EnrollStudentsModal';
import useImpersonation from '../../hooks/useImpersonation';
import useClubVocab from '../../hooks/useClubVocab';

const DAYS_SHORT = { Sunday:'Sun', Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat' };

const timeStr = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function DayDots({ days = [] }) {
  if (!days.length) return <span className="text-xs text-gray-400">—</span>;
  const active = new Set(days);
  return (
    <div className="flex items-center gap-1" title={days.map(d => d.slice(0, 3)).join(', ')}>
      {WEEK.map((day, i) => {
        const on = active.has(day);
        return (
          <span
            key={day}
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
              on ? 'bg-red-500/70 text-white' : 'bg-nav-hover text-gray-400'
            }`}
          >
            {WEEK_LETTERS[i]}
          </span>
        );
      })}
    </div>
  );
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

function ClassDetail() {
  const { isReadOnly } = useImpersonation();
  const voc = useClubVocab();
  const { classId }   = useParams();
  const { formatDate } = useCalendar();
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [showEnroll, setShowEnroll] = useState(false);
  const [confirmUnenroll, setConfirmUnenroll] = useState(null);

  const { data: clsData, isPending: clsLoading } = useQuery({
    queryKey: ['class', classId],
    queryFn:  () => getClass(classId).then(r => r.data),
  });

  const { data: enrollData, isPending: enrollLoading } = useQuery({
    queryKey: ['class-enrollments', classId],
    queryFn:  () => getClassEnrollments(classId).then(r => r.data),
  });

  const { data: reportData } = useQuery({
    queryKey: ['class-monthly-report', classId],
    queryFn:  () => getMonthlyReport({ classId }).then(r => r.data),
    enabled:  tab === 'attendance',
  });

  const unenrollMutation = useMutation({
    mutationFn: unenroll,
    onSuccess:  () => { toast.success(`${voc.member} unenrolled`); queryClient.invalidateQueries({ queryKey: ['class-enrollments', classId] }); setConfirmUnenroll(null); },
    onError:    () => toast.error('Failed to unenroll'),
  });

  const pauseMutation = useMutation({
    mutationFn: ({ id }) => pauseEnrollment(id, 'Paused by admin'),
    onSuccess:  () => { toast.success('Enrollment paused'); queryClient.invalidateQueries({ queryKey: ['class-enrollments', classId] }); },
  });

  const resumeMutation = useMutation({
    mutationFn: resumeEnrollment,
    onSuccess:  () => { toast.success('Enrollment resumed'); queryClient.invalidateQueries({ queryKey: ['class-enrollments', classId] }); },
  });

  const cls         = clsData?.class;
  const enrollments = enrollData?.enrollments || [];
  const activeCount = enrollments.filter(e => e.status === 'active').length;
  // null = no limit set — never a 30-student fallback. Nothing below computes a percentage
  // or "spots remaining" against an invented number when capacity is null.
  const capacity    = cls?.maxStudents ?? null;
  const capacitySet = cls?.maxStudents != null;
  const spotsRemaining = capacitySet ? Math.max(capacity - activeCount, 0) : null;
  const isFull = capacitySet && activeCount >= capacity;

  const filteredEnrollments = search
    ? enrollments.filter(e => e.student?.name?.toLowerCase().includes(search.toLowerCase()))
    : enrollments;

  if (clsLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-100 animate-pulse rounded-xl w-48" />
        <div className="h-32 bg-gray-100 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!cls) {
    return (
      <Card>
        <p className="text-center text-gray-400 py-10">Class not found</p>
        <div className="flex justify-center"><Button onClick={() => navigate('/admin/classes')}>Back to Classes</Button></div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Back nav */}
      <button
        onClick={() => navigate('/admin/classes')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors"
      >
        <MdArrowBack /> Back to Classes
      </button>

      {/* Hero card */}
      <div className="bg-card border border-divider rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-secondary">{cls.name}</h1>
            <p className="text-muted text-sm mt-0.5">{cls.discipline} · {cls.type}</p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-gray-500">
              <DayDots days={cls.days} />
              {cls.startTime && <span>{timeStr(cls.startTime)} – {timeStr(cls.endTime)}</span>}
              {cls.room && <span>{cls.room}</span>}
              {cls.trainer && (
                <span className="flex items-center gap-1.5">
                  <span className="text-muted">Trainer:</span>
                  <span className="text-secondary font-medium">{cls.trainer.fullName}</span>
                </span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-3xl font-black text-primary">{activeCount}</div>
            <div className="text-muted text-xs">
              {capacitySet ? `of ${capacity} enrolled` : <span className="text-subtle">enrolled · no limit set</span>}
            </div>
          </div>
        </div>
        {capacitySet && (
          <div className="mt-4 h-2 bg-nav-hover rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${Math.min((activeCount / capacity) * 100, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[['overview','Overview'],['enrolled',`Enrolled ${voc.members}`],['attendance','Attendance History']].map(([v,l]) => (
          <button
            key={v}
            onClick={() => setTab(v)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
              tab === v ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:border-primary'
            }`}
          >
            {l}
            {v === 'enrolled' && <span className="ml-1.5 bg-white/20 px-1.5 rounded-full text-xs">{activeCount}</span>}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <MdPeople className="text-blue-600 text-xl" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{activeCount}</p>
                <p className="text-xs text-gray-400">Active {voc.members.toLowerCase()}</p>
              </div>
            </div>
          </Card>
          <Card>
            {capacitySet ? (
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isFull ? 'bg-amber-500/15' : 'bg-green-100'}`}>
                  <MdCheckCircle className={`text-xl ${isFull ? 'text-amber-500' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${isFull ? 'text-amber-500' : 'text-secondary'}`}>
                    {isFull ? 'FULL' : spotsRemaining}
                  </p>
                  <p className="text-xs text-gray-400">{isFull ? `0 spots remaining` : 'Spots remaining'}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-nav-hover">
                    <MdCheckCircle className="text-xl text-subtle" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-secondary">{activeCount}</p>
                    <p className="text-xs text-gray-400">No limit set</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/admin/classes')}
                  className="text-xs font-semibold text-primary hover:underline flex-shrink-0"
                >
                  Set a limit →
                </button>
              </div>
            )}
          </Card>
          <Card>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <MdCalendarToday className="text-yellow-600 text-xl" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{cls.days?.length || 0}</p>
                <p className="text-xs text-gray-400">Days per week</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Enrolled students tab */}
      {tab === 'enrolled' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search enrolled ${voc.members.toLowerCase()}…`}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <Button
              onClick={() => setShowEnroll(true)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            >
              <MdPeople /> Enroll {voc.members}
            </Button>
          </div>

          <Card className="p-0 overflow-hidden">
            {enrollLoading ? (
              <table className="w-full"><tbody>{Array.from({length:4}).map((_,i)=><SkeletonRow key={i} cols={4}/>)}</tbody></table>
            ) : !filteredEnrollments.length ? (
              <div className="py-14 text-center">
                <MdPeople className="text-5xl text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm font-medium">No {voc.members.toLowerCase()} enrolled yet</p>
                <p className="text-gray-300 text-xs mt-1">Use the "Enroll {voc.members}" button to get started</p>
                <div className="mt-4 flex justify-center">
                  <Button
                    onClick={() => setShowEnroll(true)}
                    disabled={isReadOnly}
                    title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                  >Enroll First {voc.members}</Button>
                </div>
              </div>
            ) : (
              <>
              {/* Mobile cards (<md) — rendered from the same filteredEnrollments array */}
              <div className="md:hidden divide-y divide-divider">
                {filteredEnrollments.map(e => (
                  <div key={e._id} className="p-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 bg-primary-light text-primary rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {(e.student?.name || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-secondary truncate">{e.student?.name}</p>
                        <p className="text-sm text-muted truncate">{e.student?.email}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                        e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {e.status === 'active' ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <BeltBadge belt={e.student?.currentGrade} size="xs" />
                      <span className="text-sm text-muted">Enrolled <DualDate date={e.enrolledDate} /></span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {e.status === 'active' ? (
                        <button
                          onClick={() => pauseMutation.mutate({ id: e._id })}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                          className="inline-flex items-center gap-1.5 px-3 min-h-[40px] rounded-lg text-sm font-medium text-gray-500 border border-divider hover:bg-nav-hover hover:text-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdPause className="text-base" /> Pause
                        </button>
                      ) : (
                        <button
                          onClick={() => resumeMutation.mutate(e._id)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                          className="inline-flex items-center gap-1.5 px-3 min-h-[40px] rounded-lg text-sm font-medium text-green-600 border border-divider hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MdPlayArrow className="text-base" /> Resume
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmUnenroll(e)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                        className="inline-flex items-center gap-1.5 px-3 min-h-[40px] rounded-lg text-sm font-medium text-red-500 border border-divider hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MdPersonRemove className="text-base" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table (md+) */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-400 font-medium">
                    <th className="text-left px-5 py-3">{voc.member}</th>
                    <th className="text-left px-4 py-3 hidden sm:table-cell">Belt</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Enrolled</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-center px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnrollments.map(e => (
                    <tr key={e._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-primary-light text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {(e.student?.name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-secondary">{e.student?.name}</p>
                            <p className="text-xs text-gray-400">{e.student?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <BeltBadge belt={e.student?.currentGrade} size="xs" />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-400">
                        <DualDate date={e.enrolledDate} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {e.status === 'active' ? 'Active' : 'Paused'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {e.status === 'active' ? (
                            <button
                              onClick={() => pauseMutation.mutate({ id: e._id })}
                              disabled={isReadOnly}
                              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-nav-hover hover:text-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <MdPause className="text-base" /> Pause
                            </button>
                          ) : (
                            <button
                              onClick={() => resumeMutation.mutate(e._id)}
                              disabled={isReadOnly}
                              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-green-600 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <MdPlayArrow className="text-base" /> Resume
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmUnenroll(e)}
                            disabled={isReadOnly}
                            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <MdPersonRemove className="text-base" /> Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Attendance history tab */}
      {tab === 'attendance' && (
        <Card className="p-0 overflow-hidden">
          {!reportData?.sessions?.length ? (
            <div className="py-14 text-center text-gray-400 text-sm">No attendance data yet</div>
          ) : (
            <>
            {/* Mobile cards (<md) — rendered from the same reportData.sessions array */}
            <div className="md:hidden divide-y divide-divider">
              {reportData.sessions.map((s, i) => {
                const p = s.records.filter(r => r.status === 'present').length;
                const l = s.records.filter(r => r.status === 'late').length;
                const a = s.records.filter(r => r.status === 'absent').length;
                const total = p + l + a;
                const rate  = total ? Math.round(((p + l) / total) * 100) : 0;
                return (
                  <div key={i} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-secondary"><DualDate date={s.date} /></p>
                      <span className="text-sm font-medium text-muted">{rate}%</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-sm font-medium text-green-600 bg-green-50 px-2.5 py-0.5 rounded-full">{p} present</span>
                      <span className="text-sm font-medium text-yellow-600 bg-yellow-50 px-2.5 py-0.5 rounded-full">{l} late</span>
                      <span className="text-sm font-medium text-red-500 bg-red-50 px-2.5 py-0.5 rounded-full">{a} absent</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full mt-3">
                      <div className={`h-full rounded-full ${rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ width: `${rate}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Desktop table (md+) */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-400 font-medium">
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-4 py-3">Present</th>
                  <th className="text-left px-4 py-3">Late</th>
                  <th className="text-left px-4 py-3">Absent</th>
                  <th className="text-left px-4 py-3">Rate</th>
                </tr>
              </thead>
              <tbody>
                {reportData.sessions.map((s, i) => {
                  const p = s.records.filter(r => r.status === 'present').length;
                  const l = s.records.filter(r => r.status === 'late').length;
                  const a = s.records.filter(r => r.status === 'absent').length;
                  const total = p + l + a;
                  const rate  = total ? Math.round(((p + l) / total) * 100) : 0;
                  return (
                    <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm text-secondary"><DualDate date={s.date} /></td>
                      <td className="px-4 py-3 text-sm text-green-600 font-medium">{p}</td>
                      <td className="px-4 py-3 text-sm text-yellow-600 font-medium">{l}</td>
                      <td className="px-4 py-3 text-sm text-red-500 font-medium">{a}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full max-w-[60px]">
                            <div className={`h-full rounded-full ${rate >= 80 ? 'bg-green-500' : rate >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}
                              style={{ width: `${rate}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            </>
          )}
        </Card>
      )}

      {/* Enroll modal */}
      {showEnroll && cls && (
        <EnrollStudentsModal
          classInfo={cls}
          onClose={() => setShowEnroll(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['class-enrollments', classId] })}
        />
      )}

      {/* Unenroll confirm modal */}
      {confirmUnenroll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-secondary text-lg mb-2">Unenroll {confirmUnenroll.student?.name}?</h3>
            <p className="text-gray-400 text-sm mb-5">They will be removed from this class and won't appear in future attendance sheets.</p>
            <div className="flex gap-3">
              <Button variant="ghost" fullWidth onClick={() => setConfirmUnenroll(null)}>Cancel</Button>
              <Button
                fullWidth
                className="!bg-red-500 hover:!bg-red-600"
                loading={unenrollMutation.isPending}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                onClick={() => unenrollMutation.mutate(confirmUnenroll._id)}
              >
                Unenroll
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassDetail;
